import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    const featuredMediaId = event.detail.resource?.featured_media?.id;

    if (!newMediaGallery) return;

    if (featuredMediaId && this.#canReuseCurrentGallery(newMediaGallery) && this.#selectFeaturedMedia(featuredMediaId)) {
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Keeps the existing gallery when the variant change does not alter the media set.
   *
   * @param {Element} newMediaGallery
   * @returns {boolean}
   */
  #canReuseCurrentGallery(newMediaGallery) {
    const currentSlideIds = this.#getSlideIds(this);
    const nextSlideIds = this.#getSlideIds(newMediaGallery);

    if (!currentSlideIds.length || currentSlideIds.length !== nextSlideIds.length) return false;

    const nextSlideIdSet = new Set(nextSlideIds);
    return currentSlideIds.every((id) => nextSlideIdSet.has(id));
  }

  /**
   * @param {ParentNode} root
   * @returns {string[]}
   */
  #getSlideIds(root) {
    return Array.from(root.querySelectorAll('slideshow-slide[slide-id]'))
      .map((slide) => slide.getAttribute('slide-id'))
      .filter((id) => id);
  }

  /**
   * @param {string | number} mediaId
   * @returns {boolean}
   */
  #selectFeaturedMedia(mediaId) {
    const slideId = String(mediaId);
    const hasMatchingSlide = this.slideshow?.refs.slides?.some((slide) => slide.getAttribute('slide-id') === slideId);

    if (!hasMatchingSlide) return false;

    this.slideshow?.select({ id: slideId }, undefined, { animate: false });
    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
